// Hook para tracking de erros e performance
import { useCallback, useContext } from 'react';
import AuthContext from './useAuth';

export const useErrorTracking = (plausible) => {
  const auth = useContext(AuthContext);
  
  // Função para notificar geração de gráfico por email
  const notifyChartGenerated = useCallback(async (chartData) => {
    try {
      console.log('📧 [DEBUG] Iniciando envio de notificação de gráfico:', chartData);
      
      const notificationData = {
        chartType: chartData.type,
        chartSubtype: chartData.subtype || null,
        dataSource: chartData.isExample ? 'example' : 'user_upload',
        itemCount: chartData.itemCount || 0,
        fileSize: chartData.fileSize || null,
        fileType: chartData.fileType || null,
        processingTime: chartData.processingTime || null,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        sessionId: getSessionId(),
        url: window.location.href
      };

      console.log('📧 [DEBUG] Dados da notificação preparados:', notificationData);

      // Incluir token de autenticação se disponível
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (auth && auth.getToken) {
        const token = auth.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      console.log('📧 [DEBUG] Enviando para /api/chart-notification...');

      // Enviar notificação em background (não bloquear UI)
      fetch('/api/chart-notification', {
        method: 'POST',
        headers,
        body: JSON.stringify(notificationData)
      })
      .then(response => {
        console.log('📧 [DEBUG] Resposta recebida:', response.status);
        return response.json();
      })
      .then(data => {
        console.log('📧 [DEBUG] Dados da resposta:', data);
      })
      .catch(error => {
        // Falha silenciosa - não afetar experiência do usuário
        console.warn('Failed to send chart notification:', error);
      });

    } catch (error) {
      console.warn('Error preparing chart notification:', error);
    }
  }, [auth]);

  // Função para trackear erros de upload
  const trackFileUploadError = useCallback((error, file, chartType, retryCount = 0) => {
    let errorType = 'unknown';
    let errorDetails = {
      fileSize: file?.size || 0,
      fileType: file?.type || file?.name?.split('.').pop() || '',
      message: error?.message || error || '',
      retryCount
    };

    // Classificar tipo de erro
    if (error?.message || typeof error === 'string') {
      const errorMessage = (error?.message || error).toLowerCase();
      
      if (errorMessage.includes('format') || errorMessage.includes('extension')) {
        errorType = 'format';
      } else if (errorMessage.includes('size') || errorMessage.includes('large')) {
        errorType = 'size';
      } else if (errorMessage.includes('column') || errorMessage.includes('header')) {
        errorType = 'columns';
      } else if (errorMessage.includes('encoding') || errorMessage.includes('character')) {
        errorType = 'encoding';
      } else if (errorMessage.includes('corrupt') || errorMessage.includes('damaged')) {
        errorType = 'corrupted';
      } else if (errorMessage.includes('empty') || errorMessage.includes('no data')) {
        errorType = 'empty';
      } else {
        errorType = 'processing';
      }
    }

    if (plausible) {
      plausible.trackUploadError(errorType, chartType, errorDetails);
      plausible.trackSection('error-upload');
    }

    return { errorType, errorDetails };
  }, [plausible]);

  // Função para trackear erros de validação
  const trackDataValidationError = useCallback((chartType, data, validationResults) => {
    const results = {
      errorCount: 0,
      missingColumns: [],
      invalidDates: 0,
      invalidValues: 0,
      emptyRows: 0,
      totalRows: data?.length || 0,
      successRate: 0
    };

    // Analisar dados para encontrar erros
    if (data && Array.isArray(data)) {
      const requiredColumns = getRequiredColumns(chartType);
      const headers = data.length > 0 ? Object.keys(data[0]) : [];
      
      // Verificar colunas obrigatórias faltando
      results.missingColumns = requiredColumns.filter(col => !headers.includes(col));
      
      // Analisar qualidade dos dados
      data.forEach((row, index) => {
        let hasError = false;
        
        // Verificar linhas vazias
        const values = Object.values(row).filter(val => val !== null && val !== undefined && val !== '');
        if (values.length === 0) {
          results.emptyRows++;
          hasError = true;
        }

        // Verificar datas inválidas (para campos de data)
        const dateFields = getDateFields(chartType);
        dateFields.forEach(field => {
          if (row[field] && !isValidDate(row[field])) {
            results.invalidDates++;
            hasError = true;
          }
        });

        // Verificar valores inválidos
        const numericFields = getNumericFields(chartType);
        numericFields.forEach(field => {
          if (row[field] && isNaN(Number(row[field]))) {
            results.invalidValues++;
            hasError = true;
          }
        });

        if (hasError) {
          results.errorCount++;
        }
      });

      results.successRate = results.totalRows > 0 ? 
        ((results.totalRows - results.errorCount) / results.totalRows) * 100 : 0;
    }

    // Aplicar resultados externos se fornecidos
    if (validationResults) {
      Object.assign(results, validationResults);
    }

    if (plausible && results.errorCount > 0) {
      plausible.trackValidationError(chartType, results);
      plausible.trackSection('error-validation');
    }

    return results;
  }, [plausible]);

  // Função para trackear performance
  const trackActionPerformance = useCallback((action, startTime, details = {}) => {
    const duration = Date.now() - startTime;
    
    if (plausible) {
      plausible.trackPerformance(action, duration, details);
      
      // Track performance as custom events (não virtual pages)
      if (duration > 5000) {
        plausible.trackEvent('Performance Slow', {
          action,
          duration,
          threshold: '5000ms',
          ...details
        });
      } else if (duration < 1000) {
        plausible.trackEvent('Performance Fast', {
          action,
          duration,
          threshold: '1000ms',
          ...details
        });
      }
    }

    return duration;
  }, [plausible]);

  return {
    notifyChartGenerated,
    trackFileUploadError,
    trackDataValidationError,
    trackActionPerformance
  };
};

// Funções auxiliares
const getRequiredColumns = (chartType) => {
  const columnMap = {
    'leadtime': ['ID', 'Closed Date'],
    'throughput': ['Closed Date'],
    'roadmap': ['Nome', 'Data Inicio', 'Data Fim']
  };
  return columnMap[chartType] || [];
};

const getDateFields = (chartType) => {
  const dateMap = {
    'leadtime': ['Closed Date', 'Created Date'],
    'throughput': ['Closed Date'],
    'roadmap': ['Data Inicio', 'Data Fim']
  };
  return dateMap[chartType] || [];
};

const getNumericFields = (chartType) => {
  const numericMap = {
    'leadtime': ['Lead Time'],
    'throughput': [],
    'roadmap': []
  };
  return numericMap[chartType] || [];
};

const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

const getSessionId = () => {
  let sessionId = sessionStorage.getItem('user_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('user_session_id', sessionId);
  }
  return sessionId;
};
